import React, { useEffect, useState } from "react";
import { Modal, Box, Typography, useMediaQuery } from "@mui/material";
import {
  motion,
  AnimatePresence,
  useAnimation,
  useMotionValue,
} from "framer-motion";
import InfinityCard from "./InfinityCard";
import CharacterCard from "./CharacterCard";
import NeoButton from "./NeoButton";
import raysImage from "../@shared/assets/itemLightRays.png";
import backImage from "../@shared/assets/back-of-card.png";
import starShine from "../@shared/assets/icons/star-shine.webp";

/**
 * Shared overlay & modal feel matches TokenRewardModal
 * (same radial bg + spinning light rays).
 */

const MotionImg = motion("img");
const MotionTypography = motion(Typography);

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background:
    "radial-gradient(80% 60% at 50% 0%, rgba(124,77,255,0.28) 0%, rgba(0, 0, 0, 0.78) 70%, rgba(0,0,0,0.92) 100%)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1400,
};

const modalStyle = {
  width: 320,
  maxWidth: "90vw",
  padding: 24,
  borderRadius: 16,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",
};

const modalVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: [1.05, 1],
    opacity: 1,
    transition: { duration: 0.35, ease: "easeOut" },
  },
  exit: { scale: 0.8, opacity: 0, transition: { duration: 0.2 } },
};

// Burst effect constants (mirroring SlapCardAnimation)
const MAX_STARS_DESKTOP = 18;
const MAX_STARS_MOBILE = 10;
const MAX_DASHES_DESKTOP = 12;
const MAX_DASHES_MOBILE = 8;
const MAX_RIPPLES = 3; // 2–4 looks great

// 3D card faces
const cardContainerStyle = {
  position: "relative",
  width: 260,
  maxWidth: "75vw",
  aspectRatio: "2.5 / 3.5",
  transformStyle: "preserve-3d",
  cursor: "pointer",
  zIndex: 6000,
};

const faceStyle = {
  position: "absolute",
  inset: 0,
  borderRadius: 14,
  overflow: "hidden",
  backfaceVisibility: "hidden",
  WebkitBackfaceVisibility: "hidden",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

export default function CardRewardModal({
  visible,
  reward,
  onClose,
  viewOnly = false,
}) {
  const [hasRevealed, setHasRevealed] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [tapLocked, setTapLocked] = useState(false);
  const [canHoverTilt, setCanHoverTilt] = useState(false);
  const [isInspecting, setIsInspecting] = useState(false);

  // Burst FX state
  const [stars, setStars] = useState([]); // {id, dx, dy, size, dur, delay, spin}
  const [dashes, setDashes] = useState([]); // {id, dx, dy, len, th, rot, dur, delay}
  const [ripples, setRipples] = useState([]); // {id, scaleTo, dur, delay, bwStart, bwEnd, opacity}

  const isMobile = useMediaQuery("(max-width:900px)");

  const cardControls = useAnimation();
  const raysControls = useAnimation();

  // single hue per modal session for dash tint
  const [hue] = useState(() => Math.floor(Math.random() * 360));

  // 3D hover tilt (X only, so we don't fight rotateY flip)
  const rotateX = useMotionValue(0);

  const isCharacter =
    reward?.reward?.type === "character" || reward?.reward?.attackName;

  const isFinite =
    reward?.reward?.finite === true || reward?.reward?.finite === "TRUE";

  const amount = reward?.amount || 1;
  const cardHeight = isMobile ? 300 : 360;

  const renderFrontCard = () => {
    if (isCharacter) {
      return (
        <CharacterCard
          {...reward.reward}
          heightPer={94}
          containerHeight={cardHeight}
          borderPx={5}
          shineEffect={true}
          showAmtOwned={false}
        />
      );
    }

    return (
      <InfinityCard
        {...reward.reward}
        heightPer={92}
        containerHeight={cardHeight}
        borderPx={5}
        shineEffect={true}
        showAmountOwned={false}
      />
    );
  };

  // ----- Idle float animation -----
  const startFloat = async () => {
    await cardControls.start({
      y: [0, -10, 0],
      scale: [1.0, 1.04, 1.0],
      rotateZ: [-1.2, 1.2, -1.2],
      transition: {
        duration: 1.6,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "mirror",
      },
    });
  };

  // ----- Burst helpers (Star / Dash / Ripple) -----

  function spawnStars(deviceType = "desktop") {
    const count =
      deviceType === "mobile" ? MAX_STARS_MOBILE : MAX_STARS_DESKTOP;

    const items = Array.from({ length: count }).map((_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const radius = 120 + Math.random() * 160;
      const size = 10 + Math.floor(Math.random() * 14);
      const dur = 0.35 + Math.random() * 0.3;
      const delay = 0.02 + Math.random() * 0.08;
      const spin = (Math.random() * 120 - 60) | 0;

      const dx = Math.cos(angle) * radius;
      const dy = Math.sin(angle) * radius;

      return {
        id: `star-${Date.now()}-${i}`,
        dx,
        dy,
        size,
        dur,
        delay,
        spin,
      };
    });

    setStars(items);

    // tidy after they finish
    setTimeout(() => setStars([]), 900);
  }

  function spawnDashes(deviceType = "desktop") {
    const count =
      deviceType === "mobile" ? MAX_DASHES_MOBILE : MAX_DASHES_DESKTOP;

    const items = Array.from({ length: count }).map((_, i) => {
      const angle = Math.random() * Math.PI * 2;

      const travel = 120 + Math.random() * 160;

      const len = 60 + Math.random() * 120;

      const th = 2 + Math.random() * 3;

      const dur = 0.28 + Math.random() * 0.25;
      const delay = 0.01 + Math.random() * 0.08;

      const rot = (angle * 180) / Math.PI;

      const dx = Math.cos(angle) * travel;
      const dy = Math.sin(angle) * travel;

      return {
        id: `dash-${Date.now()}-${i}`,
        dx,
        dy,
        len,
        th,
        rot,
        dur,
        delay,
      };
    });

    setDashes(items);

    setTimeout(() => setDashes([]), 900);
  }

  function spawnRipples() {
    const count = Math.max(2, Math.min(4, MAX_RIPPLES));
    const items = Array.from({ length: count }).map((_, i) => {
      const scaleTo = 2.2 + Math.random() * 1.1;
      const dur = 0.38 + Math.random() * 0.22;
      const delay = i * (0.05 + Math.random() * 0.03);
      const bwStart = 18 + Math.floor(Math.random() * 8);
      const bwEnd = 2;
      const opacity = 0.65;

      return {
        id: `ripple-${Date.now()}-${i}`,
        scaleTo,
        dur,
        delay,
        bwStart,
        bwEnd,
        opacity,
      };
    });

    setRipples(items);

    setTimeout(() => setRipples([]), 1000);
  }

  // ----- 3D hover handlers -----
  const handleHoverMove = (e) => {
    // Only tilt after reveal + after the delay, and not while inspecting
    if (!hasRevealed || !canHoverTilt || isInspecting) return;

    // Avoid weirdness on mobile / touch
    if (typeof window !== "undefined" && window.innerWidth < 900) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const xPos = e.clientX - rect.left;
    const yPos = e.clientY - rect.top;

    // Normalize to [-1, 1]
    const xPercent = (xPos / rect.width) * 2 - 1;
    const yPercent = (yPos / rect.height) * 2 - 1;

    const MAX_TILT = 10; // degrees

    // We invert y so hovering top tilts it "back"
    rotateX.set(-yPercent * MAX_TILT);
    // (We’re not touching rotateY here, so flip stays clean.)
  };

  const handleHoverLeave = () => {
    if (!hasRevealed) return;
    rotateX.set(0);
  };

  // ----- Reset & intro when modal opens -----
  useEffect(() => {
    if (!visible) return;
    const startsRevealed = Boolean(viewOnly);

    setHasRevealed(startsRevealed);
    setTapLocked(false);
    setImagesLoaded(false);
    setStars([]);
    setDashes([]);
    setRipples([]);
    setCanHoverTilt(false);
    setIsInspecting(false);
    rotateX.set(0);

    // baseline pose: face-down card in center, fade/scale in
    cardControls.set({
      x: 0,
      y: 10,
      scale: 0.95,
      rotateY: startsRevealed && !viewOnly ? 180 : 0, // front if using flip faces
      rotateX: 0,
      rotateZ: 0,
      opacity: 0,
    });

    (async () => {
      await cardControls.start({
        opacity: 1,
        y: [30, -6, 0],
        scale: [0.9, 1.05, 1.0],
        transition: { duration: 0.45, ease: "easeOut" },
      });
      // then begin idle float loop
      startFloat();

      if (startsRevealed) {
        setTimeout(() => {
          setCanHoverTilt(true);
        }, 2000);
      }
    })();
  }, [visible, reward, cardControls, rotateX, viewOnly]);

  // ----- Rays spin once image is loaded -----
  const handleRaysLoaded = () => {
    setImagesLoaded(true);
  };

  useEffect(() => {
    if (!visible) {
      raysControls.set({ rotate: 0 });
      return;
    }

    if (visible && imagesLoaded) {
      raysControls.start({
        rotate: 360,
        transition: { repeat: Infinity, duration: 6, ease: "linear" },
      });
    }
  }, [visible, imagesLoaded, raysControls]);

  // ----- Reveal sequence: slide → flip → slap → float (front) -----
  const handleRevealTap = async () => {
    if (hasRevealed || tapLocked || !visible) return;
    setTapLocked(true);
    setHasRevealed(true);
    setCanHoverTilt(false);
    setIsInspecting(false);
    rotateX.set(0);

    // Stop float by starting a non-looping animation.
    await cardControls.start({
      y: 0,
      scale: 1,
      rotateZ: 0,
      transition: { duration: 0.1 },
    });

    // PHASE 1: Slide + flip toward center
    const flipDuration = 0.55;
    cardControls.start({
      x: [-16, -90, 0],
      y: [0, -12, 4],
      scale: [1.02, 1.1, 1.02],
      rotateY: [0, 100, 180],
      transition: {
        duration: flipDuration,
        ease: [0.22, 0.61, 0.36, 1],
        times: [0, 0.55, 1],
      },
    });

    // PHASE 2: Impact / slap in center, with shake + bursts
    setTimeout(() => {
      cardControls.start({
        y: [4, 10, 0],
        scale: [1.02, 0.97, 1.03],
        transition: {
          duration: 0.2,
          ease: [0.22, 0.61, 0.36, 1],
          times: [0, 0.5, 1],
        },
      });

      // subtle shake on impact
      cardControls.start({
        x: [0, 8, -8, 4, -2, 0],
        transition: { duration: 0.22, ease: "easeInOut" },
      });

      // spawn burst FX right at impact
      const deviceType = isMobile ? "mobile" : "desktop";
      spawnRipples();
      spawnDashes(deviceType);
      spawnStars(deviceType);
    }, flipDuration * 1000 - 80);

    // After the slap finishes, resume idle float with the card now face-up (rotateY=180)
    const endOfSlapMs = flipDuration * 1000 + 260;

    setTimeout(() => {
      startFloat();
      setTapLocked(false);

      // enable hover tilt 2s after float resumes
      setTimeout(() => {
        setCanHoverTilt(true);
      }, 2000);
    }, endOfSlapMs);
  };

  // ----- Inspection toggle: zoom in / zoom out -----
  const handleToggleInspect = async () => {
    if (!hasRevealed || tapLocked) return;

    if (isInspecting) {
      // Exit inspect: return to float, UI fades back in
      setIsInspecting(false);
      rotateX.set(0);
      cardControls.stop();
      startFloat();
      setCanHoverTilt(true);
    } else {
      // Enter inspect: zoom card, stop float, disable hover tilt
      setIsInspecting(true);
      setCanHoverTilt(false);
      rotateX.set(0);
      cardControls.stop();
      await cardControls.start({
        y: 0,
        scale: 1.5,
        rotateZ: 0,
        transition: { duration: 0.25, ease: "easeOut" },
      });
    }
  };

  // Card click handler: reveal first, inspect afterward
  const handleCardClick = () => {
    if (!hasRevealed) {
      handleRevealTap();
    } else {
      handleToggleInspect();
    }
  };

  // ----- Slingshot exit when pressing Continue -----
  const handleContinue = async () => {
    if (tapLocked) return;
    setTapLocked(true);

    // Ensure we’re not in inspect / hover tilt state
    setIsInspecting(false);
    setCanHoverTilt(false);
    rotateX.set(0);

    // Stop any floating loop
    cardControls.stop();

    try {
      // Small wind-up down
      await cardControls.start({
        y: 18,
        scale: 1.06,
        rotateZ: 0,
        transition: {
          duration: 0.12,
          ease: "easeIn",
        },
      });

      // Fast sling upward off-screen
      await cardControls.start({
        y: -600,
        scale: 0.9,
        opacity: 0,
        transition: {
          duration: 0.3,
          ease: [0.11, 0.5, 0.3, 1],
        },
      });
    } finally {
      // After animation completes, run original close logic
      onClose && onClose();
    }
  };

  if (!reward?.reward) return null;

  return (
    <Modal open={visible} onClose={onClose} closeAfterTransition>
      <AnimatePresence>
        {visible && (
          <Box
            component={motion.div}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={modalVariants}
            style={overlayStyle}
          >
            <Box sx={modalStyle}>
              {/* Spinning light rays behind card */}
              <MotionImg
                src={raysImage}
                onLoad={handleRaysLoaded}
                animate={raysControls}
                style={{
                  position: "absolute",
                  width: 480,
                  height: 480,
                  zIndex: -1,
                  opacity: 0.7,
                  top: "20%",
                  left: "16%",
                  transform: "translateX(-50%)",
                  pointerEvents: "none",
                }}
              />

              {/* Ripple rings (behind card) */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  pointerEvents: "none",
                  zIndex: 5900,
                }}
              >
                {ripples.map((r) => (
                  <motion.div
                    key={r.id}
                    initial={{
                      scale: 0.2,
                      opacity: 0,
                      borderWidth: r.bwStart,
                    }}
                    animate={{
                      scale: r.scaleTo,
                      opacity: [0, r.opacity, 0],
                      borderWidth: r.bwEnd,
                    }}
                    transition={{
                      duration: r.dur,
                      delay: r.delay,
                      ease: [0.22, 0.61, 0.36, 1],
                    }}
                    style={{
                      position: "absolute",
                      width: 220,
                      height: 220,
                      borderRadius: "50%",
                      borderStyle: "solid",
                      borderColor: "rgba(255,255,255,0.8)",
                      boxShadow:
                        "0 0 20px rgba(255,255,255,0.45), inset 0 0 12px rgba(255,255,255,0.35)",
                    }}
                  />
                ))}
              </div>

              {/* Speed dashes (above card, below stars) */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  pointerEvents: "none",
                  zIndex: 9050,
                }}
              >
                {dashes.map((d) => (
                  <motion.div
                    key={d.id}
                    initial={{
                      x: 0,
                      y: 0,
                      opacity: 0,
                      width: 0,
                      height: d.th,
                      rotate: d.rot,
                      scaleY: 1,
                      filter:
                        "drop-shadow(0 0 6px rgba(255,255,255,0.6))",
                    }}
                    animate={{
                      x: d.dx,
                      y: d.dy,
                      width: [0, d.len, d.len * 0.8],
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: d.dur,
                      delay: d.delay,
                      ease: [0.22, 0.61, 0.36, 1],
                    }}
                    style={{
                      position: "absolute",
                      background:
                        "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 45%, rgba(255,255,255,0) 100%)",
                      borderRadius: d.th,
                      filter: `hue-rotate(${hue}deg) brightness(1.1)`,
                    }}
                  />
                ))}
              </div>

              {/* Stars burst (above dashes) */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  pointerEvents: "none",
                  zIndex: 9100,
                }}
              >
                {stars.map((s) => (
                  <motion.img
                    key={s.id}
                    src={starShine}
                    alt=""
                    initial={{
                      x: 0,
                      y: 0,
                      opacity: 0,
                      scale: 0.6,
                      rotate: 0,
                    }}
                    animate={{
                      x: s.dx,
                      y: s.dy,
                      opacity: [0, 1, 0],
                      scale: [0.6, 1, 0.8],
                      rotate: s.spin,
                    }}
                    transition={{
                      duration: s.dur,
                      ease: [0.22, 0.61, 0.36, 1],
                      delay: s.delay,
                    }}
                    style={{
                      width: s.size,
                      height: s.size,
                      position: "absolute",
                    }}
                  />
                ))}
              </div>

              {/* Title (fades out in inspect mode) */}
              <MotionTypography
                variant="h6"
                animate={{ opacity: isInspecting ? 0 : 1 }}
                transition={{ duration: 0.2 }}
                sx={{
                  color: "#fff",
                  fontWeight: "bold",
                  mb: 1,
                  textAlign: "center",
                  textShadow:
                    "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
                }}
              >
                {viewOnly ? "Tap To Expand" : "You Received"}
              </MotionTypography>

              {/* Card + Tap-to-Reveal / Inspect */}
              <Box
                sx={{
                  position: "relative",
                  mt: 1,
                  mb: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <motion.div
                  animate={cardControls}
                  initial={{
                    x: 0,
                    y: 10,
                    scale: 0.95,
                    rotateY: 0,
                    opacity: 1,
                  }}
                  style={{
                    ...cardContainerStyle,
                    rotateX,
                    transformPerspective: 800,
                  }}
                  onMouseMove={handleHoverMove}
                  onMouseLeave={handleHoverLeave}
                  onClick={handleCardClick}
                >
                  {viewOnly ? (
                    <div
                      style={{
                        ...faceStyle,
                        transform: "none",
                      }}
                    >
                      {renderFrontCard()}
                    </div>
                  ) : (
                    <>
                      {/* Back face */}
                      <div
                        style={{
                          ...faceStyle,
                          transform: "rotateY(0deg)",
                          backgroundColor: "#000",
                        }}
                      >
                        <img
                          src={backImage}
                          alt="card back"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>

                      {/* Front face */}
                      <div
                        style={{
                          ...faceStyle,
                          transform: "rotateY(180deg)",
                        }}
                      >
                        {renderFrontCard()}
                      </div>
                    </>
                  )}
                </motion.div>

                {/* Pulsing "Tap to Reveal" text (only before reveal) */}
                {!hasRevealed && (
                  <motion.div
                    animate={{
                      scale: [1, 1.08, 1],
                      opacity: [0.7, 1, 0.7],
                    }}
                    transition={{
                      duration: 1.1,
                      repeat: Infinity,
                      repeatType: "loop",
                    }}
                    style={{
                      position: "absolute",
                      bottom: "50%",
                      left: "30%",
                      transform: "translateX(-50%)",
                      pointerEvents: "none",
                      zIndex: 9999,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        letterSpacing: 1.2,
                        color: "#fbcf57",
                        textTransform: "uppercase",
                        textShadow:
                          "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
                      }}
                    >
                      Tap to Reveal
                    </Typography>
                  </motion.div>
                )}
              </Box>

              {/* Card type + amount (fade out in inspect mode) */}
              <MotionTypography
                animate={{ opacity: isInspecting ? 0 : 1 }}
                transition={{ duration: 0.2 }}
                sx={{
                  color: "#fbcf57",
                  fontSize: 18,
                  fontWeight: 600,
                  mt: hasRevealed ? 1 : 3,
                  textAlign: "center",
                  textShadow:
                    "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
                }}
              >
                {isCharacter
                  ? "Character Card"
                  : isFinite
                  ? "Finite Card"
                  : "Infinity Card"}
              </MotionTypography>

              <MotionTypography
                animate={{ opacity: isInspecting ? 0 : 1 }}
                transition={{ duration: 0.2 }}
                sx={{
                  color: "#fff",
                  fontSize: 16,
                  mb: 2,
                  textAlign: "center",
                  textShadow:
                    "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
                }}
              >
                x{amount}
              </MotionTypography>

              {/* Continue button only after reveal (fade out in inspect mode) */}
              {hasRevealed && (
                <motion.div
                  animate={{ opacity: isInspecting ? 0 : 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <NeoButton
                    onClick={handleContinue}
                    text={"Continue"}
                    width={100}
                    color={"#e0b44c"}
                    disabled={tapLocked}
                  />
                </motion.div>
              )}
            </Box>
          </Box>
        )}
      </AnimatePresence>
    </Modal>
  );
}
